import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as Anchor } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faBoxOpen,
    faCartShopping,
    faPaperPlane,
    faSearch,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import botHaimpressAvatar from '../../images/bot-haimpress-avatar.jpg';
import { catalogImageUrl } from '../imageUrl';
import { useStoreResource } from '../storeData';
import './BotHaimpress.css';
import {
    BOT_NAME,
    addSimpleProductToCart,
    buildBotAnswer,
    buildCatalog,
    formatCurrency,
    getProductImage,
    isOutOfStock,
    openCartFromBot,
    productNeedsOptions,
    productUrl,
} from './botHaimpressUtils';

const NUDGE_KEY = 'bot-haimpress-nudge-seen';

export default function BotHaimpress() {
    const { data: productos, loading: loadingProductos } = useStoreResource('productos', []);
    const { data: secciones, loading: loadingSecciones } = useStoreResource('secciones', []);
    const { data: categorias, loading: loadingCategorias } = useStoreResource('categorias', []);
    const { data: subcategorias, loading: loadingSubcategorias } = useStoreResource('subcategorias', []);
    const { data: tiendaList } = useStoreResource('tienda', []);

    const [isOpen, setIsOpen] = useState(false);
    const [showNudge, setShowNudge] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState([
        {
            id: 'welcome',
            from: 'bot',
            text: `Hola, soy ${BOT_NAME} de ShopHai.online. Te ayudo a encontrar ese producto que tienes en mente y comprar sin perderte.`,
            actions: [
                { label: 'Ver ofertas', value: 'ofertas' },
                { label: 'Como comprar', value: 'como comprar' },
                { label: 'Hablar por WhatsApp', action: 'open-whatsapp', whatsappMessage: 'Hola, necesito ayuda desde ShopHai.online.' },
                { label: 'Ver productos', value: 'productos', url: '/productos' },
            ],
        },
    ]);
    const messagesRef = useRef(null);

    const catalog = useMemo(() => buildCatalog({
        productos,
        secciones,
        categorias,
        subcategorias,
    }), [productos, secciones, categorias, subcategorias]);

    const catalogLoading = loadingProductos || loadingSecciones || loadingCategorias || loadingSubcategorias;
    const tiendaPrincipal = tiendaList?.[0] || null;

    useEffect(() => {
        try {
            if (sessionStorage.getItem(NUDGE_KEY) !== '1') {
                setShowNudge(true);
            }
        } catch (error) {
            setShowNudge(true);
        }
    }, []);

    useEffect(() => {
        if (!messagesRef.current) return;
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }, [messages, isOpen]);

    const openBot = () => {
        setIsOpen(true);
        setShowNudge(false);
        try {
            sessionStorage.setItem(NUDGE_KEY, '1');
        } catch (error) {
            // El saludo solo depende de sessionStorage cuando el navegador lo permite.
        }
    };

    const closeBot = () => {
        setIsOpen(false);
    };

    const dismissNudge = () => {
        setShowNudge(false);
        try {
            sessionStorage.setItem(NUDGE_KEY, '1');
        } catch (error) {
            // Sin almacenamiento disponible, solo cerramos el aviso visual.
        }
    };

    const sendMessage = (rawText) => {
        const text = rawText.trim();
        if (!text) return;

        const userMessage = {
            id: `${Date.now()}-user`,
            from: 'user',
            text,
        };

        const botMessage = catalogLoading
            ? {
                id: `${Date.now()}-bot`,
                from: 'bot',
                text: 'Estoy cargando el catalogo. Intenta de nuevo en un momento.',
                actions: [],
            }
            : {
                id: `${Date.now()}-bot`,
                from: 'bot',
                ...buildBotAnswer(text, catalog),
            };

        setMessages(currentMessages => [...currentMessages, userMessage, botMessage]);
        setInputValue('');
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        sendMessage(inputValue);
    };

    const handleAction = (action) => {
        if (action.action === 'open-cart') {
            openCartFromBot();
            setIsOpen(false);
            return;
        }

        if (action.action === 'open-whatsapp') {
            openWhatsApp(action.whatsappMessage);
            return;
        }

        if (action.value) {
            sendMessage(action.value);
        }
    };

    const openWhatsApp = (message) => {
        const phoneNumber = String(tiendaPrincipal?.telefono || '').replace(/[^\d]/g, '');

        if (!phoneNumber) {
            setMessages(currentMessages => [
                ...currentMessages,
                {
                    id: `${Date.now()}-bot-no-phone`,
                    from: 'bot',
                    text: 'No encontre un numero de WhatsApp configurado para la tienda. Puedes seguir buscando productos aqui.',
                    actions: [{ label: 'Ver productos', value: 'productos', url: '/productos' }],
                },
            ]);
            return;
        }

        const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message || 'Hola, necesito ayuda desde ShopHai.online.')}`;
        window.open(whatsappUrl, '_blank');
        setIsOpen(false);
    };

    const handleAddProduct = (product) => {
        if (productNeedsOptions(product)) {
            setMessages(currentMessages => [
                ...currentMessages,
                {
                    id: `${Date.now()}-bot-options`,
                    from: 'bot',
                    text: 'Ese producto tiene opciones. Abre el producto y elige las opciones antes de agregarlo.',
                    products: [],
                    navigationTargets: [{ label: 'Elegir opciones', url: productUrl(product) }],
                    actions: [],
                },
            ]);
            return;
        }

        if (isOutOfStock(product)) {
            setMessages(currentMessages => [
                ...currentMessages,
                {
                    id: `${Date.now()}-bot-stock`,
                    from: 'bot',
                    text: 'Ese producto aparece agotado. Puedes mirar otros productos relacionados.',
                    actions: [{ label: 'Ver productos', value: 'productos', url: '/productos' }],
                },
            ]);
            return;
        }

        addSimpleProductToCart(product);
        setMessages(currentMessages => [
            ...currentMessages,
            {
                id: `${Date.now()}-bot-added`,
                from: 'bot',
                text: `Listo, agregue ${product.titulo} al carrito.`,
                actions: [
                    { label: 'Ver carrito', value: 'ver carrito', action: 'open-cart' },
                    { label: 'Seguir buscando', value: 'productos', url: '/productos' },
                ],
            },
        ]);
    };

    return (
        <div className='botHaimpressRoot'>
            {showNudge && !isOpen && (
                <div className='botHaimpressNudge'>
                    <button type='button' className='botHaimpressNudgeClose' onClick={dismissNudge} aria-label='Cerrar saludo'>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <button type='button' className='botHaimpressNudgeText' onClick={openBot}>
                        <strong>{BOT_NAME}</strong>
                        <span>ShopHai.online: tu tienda te ayuda a escoger.</span>
                    </button>
                </div>
            )}

            {isOpen && (
                <section className='botHaimpressPanel' aria-label={BOT_NAME}>
                    <div className='botHaimpressHeader'>
                        <div className='botHaimpressIdentity'>
                            <span className='botHaimpressAvatar'>
                                <img src={botHaimpressAvatar} alt={BOT_NAME} loading='eager' decoding='async' />
                            </span>
                            <div>
                                <h3>{BOT_NAME}</h3>
                                <p>ShopHai.online | compra guiada</p>
                            </div>
                        </div>
                        <button type='button' onClick={closeBot} aria-label='Cerrar chat'>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className='botHaimpressBrandStrip'>
                        Tu tienda, tu compra mas facil.
                    </div>

                    <div className='botHaimpressMessages' ref={messagesRef}>
                        {messages.map(message => (
                            <article className={`botHaimpressMessage ${message.from === 'user' ? 'isUser' : 'isBot'}`} key={message.id}>
                                <p>{message.text}</p>
                                {message.navigationTargets?.length > 0 && (
                                    <div className='botHaimpressNavTargets'>
                                        {message.navigationTargets.map(target => (
                                            <Anchor key={target.url} to={target.url} onClick={closeBot}>
                                                <FontAwesomeIcon icon={faArrowRight} />
                                                <span>{target.title || target.label}</span>
                                            </Anchor>
                                        ))}
                                    </div>
                                )}
                                {message.products?.length > 0 && (
                                    <div className='botHaimpressProducts'>
                                        {message.products.map(product => (
                                            <ProductSuggestion
                                                key={product.idProducto}
                                                product={product}
                                                onAdd={handleAddProduct}
                                                onNavigate={closeBot}
                                            />
                                        ))}
                                    </div>
                                )}
                                {message.actions?.length > 0 && (
                                    <div className='botHaimpressActions'>
                                        {message.actions.map(action => (
                                            action.url ? (
                                                <Anchor key={`${message.id}-${action.label}`} to={action.url} onClick={closeBot}>
                                                    {action.label}
                                                </Anchor>
                                            ) : (
                                                <button
                                                    type='button'
                                                    key={`${message.id}-${action.label}`}
                                                    onClick={() => handleAction(action)}
                                                >
                                                    {action.label}
                                                </button>
                                            )
                                        ))}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>

                    <form className='botHaimpressComposer' onSubmit={handleSubmit}>
                        <FontAwesomeIcon icon={faSearch} />
                        <input
                            type='text'
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                            placeholder='Buscar producto...'
                            aria-label='Buscar producto con BOT-HAIMPRESS'
                        />
                        <button type='submit' aria-label='Enviar mensaje'>
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                    </form>
                </section>
            )}

            <button
                type='button'
                className={`botHaimpressLauncher ${isOpen ? 'isOpen' : ''}`}
                onClick={isOpen ? closeBot : openBot}
                aria-label={isOpen ? 'Cerrar BOT-HAIMPRESS' : 'Abrir BOT-HAIMPRESS'}
                aria-expanded={isOpen}
            >
                {isOpen ? (
                    <FontAwesomeIcon icon={faTimes} />
                ) : (
                    <img src={botHaimpressAvatar} alt='' aria-hidden='true' loading='eager' decoding='async' />
                )}
            </button>
        </div>
    );
}

function ProductSuggestion({ product, onAdd, onNavigate }) {
    const image = getProductImage(product);
    const needsOptions = productNeedsOptions(product);
    const outOfStock = isOutOfStock(product);

    return (
        <div className='botHaimpressProduct'>
            {image ? (
                <img src={catalogImageUrl(image)} alt={product.titulo} loading='lazy' decoding='async' />
            ) : (
                <div className='botHaimpressProductImageFallback'>
                    <FontAwesomeIcon icon={faBoxOpen} />
                </div>
            )}
            <div className='botHaimpressProductInfo'>
                <h4>{product.titulo}</h4>
                <span className='botHaimpressProductPrice'>{formatCurrency(product.precio)}</span>
                {outOfStock && <small>Agotado</small>}
                <div className='botHaimpressProductActions'>
                    <Anchor to={productUrl(product)} onClick={onNavigate}>
                        Ver producto
                    </Anchor>
                    {needsOptions || outOfStock ? (
                        <Anchor to={productUrl(product)} onClick={onNavigate}>
                            {outOfStock ? 'Consultar' : 'Elegir opciones'}
                        </Anchor>
                    ) : (
                        <button
                            type='button'
                            className='botHaimpressCartAction'
                            onClick={() => onAdd(product)}
                            aria-label={`Agregar ${product.titulo} al carrito`}
                            title='Agregar al carrito'
                        >
                            <FontAwesomeIcon icon={faCartShopping} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
